/**
 * MapLibre wrapper: basemaps (OpenFreeMap vector styles localised to Vietnamese, Esri imagery),
 * planning raster overlays served by our proxy (/api/tile), vector highlight / parcel / measure
 * overlays, selection pin and POI markers. Overlays survive basemap switches.
 */
import {
  BASEMAP_BY_ID, IMAGERY_TILES, IMAGERY_TILES_KEYED, GLYPHS, ATTR_PLAN, ATTR_ESRI, ATTR_ESRI_KEYED, ATTR_SDD, HANOI_BBOX, ORIGIN, MAX_BOUNDS,
} from './config.js';
import { icon, reducedMotion } from './ui.js';
import { buildDims } from './dims.js';
import { clientConfig } from './api.js';
import { PLACE_GROUPS, placeIcon } from './placestyle.js';
import { SDD } from './sddlayer.js';
import { CLASS_STEPS, CLASS_COLORS } from './core/gia.js';
import { GT, registerTransportProtocol, transportTiles, transportLayers, transportSymbols, transportColors, OMT_URL } from './transport.js';
import { loadVectorManifest, registerVectorProtocol, vectorManifest } from './vectorplan.js';
import { VLAYER, vectorLayerOf, fillColorExpr, vectorOpacity } from './core/vplan.js';

const ML = () => window.maplibregl;
const EMPTY = { type: 'FeatureCollection', features: [] };
/** line layers: skip points and fill-only polygons (properties.outline === false) */
const OUTLINE = ['all', ['!=', ['geometry-type'], 'Point'], ['!=', ['get', 'outline'], false]];

/* ---------------- basemap styles ---------------- */

const styleCache = new Map();
async function fetchStyle(url) {
  if (!styleCache.has(url)) {
    styleCache.set(url, fetch(url).then((r) => { if (!r.ok) throw new Error('style ' + r.status); return r.json(); })
      .catch((e) => { styleCache.delete(url); throw e; }));
  }
  return structuredClone(await styleCache.get(url));
}

/** Prefer Vietnamese names; keep house numbers / road refs untouched. */
const NAME_EXPR = ['coalesce', ['get', 'name:vi'], ['get', 'name'], ['get', 'name:latin'], ['get', 'name_en']];

/**
 * Ordering comparisons on a possibly-missing attribute (e.g. [">=", ["get", "admin_level"], 3])
 * make MapLibre log "Expected value to be of type number, but found null". Wrap the getter in a
 * number assertion whose fallback keeps the comparison false, so rendering is identical and
 * the console stays clean.
 */
const ORDER_OPS = new Set(['<', '<=', '>', '>=']);
function safeCompare(e) {
  if (!Array.isArray(e)) return e;
  if (ORDER_OPS.has(e[0]) && e.length === 3) {
    const greater = e[0] === '>' || e[0] === '>=';
    const fix = (x, other, right) => {
      if (Array.isArray(x) && x[0] === 'get' && x.length === 2 && typeof other === 'number') {
        return ['number', x, greater !== right ? -1e9 : 1e9];
      }
      return safeCompare(x);
    };
    return [e[0], fix(e[1], e[2], false), fix(e[2], e[1], true)];
  }
  return e.map(safeCompare);
}

function localize(style) {
  // Labels on top, everything else below: some styles start their symbol block early (dark:
  // water_name before buildings / roads), which would put buildings and roads above the
  // planning raster that is inserted under the first symbol layer. 3D extrusions are dropped
  // (the map never pitches; flat building fills remain).
  const layers = (style.layers || []).filter((l) => l.type !== 'fill-extrusion');
  style.layers = [...layers.filter((l) => l.type !== 'symbol'), ...layers.filter((l) => l.type === 'symbol')];
  for (const l of style.layers) {
    if (Array.isArray(l.filter)) l.filter = safeCompare(l.filter);
    for (const group of [l.layout, l.paint]) {
      if (!group) continue;
      for (const k of Object.keys(group)) if (Array.isArray(group[k])) group[k] = safeCompare(group[k]);
    }
    if (l.type !== 'symbol' || !l.layout || l.layout['text-field'] == null) continue;
    const s = JSON.stringify(l.layout['text-field']);
    if (/name/.test(s)) l.layout['text-field'] = NAME_EXPR;
  }
  return style;
}

async function imageryStyle() {
  const { esriKey } = await clientConfig();
  const key = typeof esriKey === 'string' && /^[\w.-]{20,400}$/.test(esriKey) ? esriKey : null;
  return {
    version: 8,
    glyphs: GLYPHS,
    sources: {
      'esri-imagery': key
        ? { type: 'raster', tiles: [IMAGERY_TILES_KEYED + encodeURIComponent(key)], tileSize: 256, maxzoom: 19, attribution: ATTR_ESRI_KEYED }
        : { type: 'raster', tiles: [IMAGERY_TILES], tileSize: 256, maxzoom: 19, attribution: ATTR_ESRI },
    },
    layers: [
      { id: 'bg', type: 'background', paint: { 'background-color': '#0d1726' } },
      { id: 'imagery', type: 'raster', source: 'esri-imagery', paint: { 'raster-fade-duration': 180 } },
    ],
  };
}

async function hybridStyle(labelsUrl) {
  const [s, img] = await Promise.all([fetchStyle(labelsUrl).then(localize), imageryStyle()]);
  const keep = s.layers.filter((l) => l.type === 'symbol' && !/housenumber|poi/.test(l.id));
  for (const l of keep) {
    l.paint = { ...(l.paint || {}), 'text-color': '#ffffff', 'text-halo-color': 'rgba(8, 14, 24, 0.86)', 'text-halo-width': 1.4, 'text-halo-blur': 0.4 };
  }
  // faint road casing for orientation over imagery
  const roads = s.layers.filter((l) => l.type === 'line' && /^(road|highway|bridge|tunnel)_(motorway|trunk|primary|secondary)(?!.*casing)/.test(l.id))
    .map((l) => ({ ...l, paint: { ...l.paint, 'line-color': 'rgba(255, 236, 170, 0.55)', 'line-opacity': 0.6 } }));
  return { ...s, sources: { ...s.sources, ...img.sources }, layers: [...img.layers, ...roads, ...keep] };
}

export async function basemapStyle(id) {
  const b = BASEMAP_BY_ID[id] || BASEMAP_BY_ID.positron;
  if (b.kind === 'imagery') return imageryStyle();
  if (b.kind === 'hybrid') return hybridStyle(b.labels);
  return localize(await fetchStyle(b.url));
}

/* ---------------- colours ---------------- */

function overlayColors(basemap) {
  const dark = document.documentElement.dataset.theme === 'dark';
  const img = /imagery|hybrid/.test(basemap);
  return {
    accent: img ? '#fde047' : dark ? '#6b9bff' : '#0052ff',
    casing: img ? 'rgba(10, 11, 13, 0.85)' : dark ? 'rgba(10, 11, 13, 0.9)' : 'rgba(255, 255, 255, 0.95)',
    project: img ? '#f472b6' : dark ? '#f472b6' : '#be185d',
    measure: img ? '#fde047' : dark ? '#fbbf24' : '#d97706',
    // decision-only plans: amber, like their "Chỉ có quyết định" tag, never the boundary magenta
    approx: img ? '#fbbf24' : dark ? '#fbbf24' : '#b45309',
  };
}

/** land-use label / dimension text colours for the current basemap */
function labelColors(basemap) {
  const dark = document.documentElement.dataset.theme === 'dark';
  const img = /imagery|hybrid/.test(basemap);
  if (img || dark || basemap === 'dark') return { text: '#f4f6f8', muted: '#c3cad3', halo: 'rgba(10, 11, 13, 0.88)', dim: img ? '#fde047' : '#9cbcff', row: img ? '#93c5fd' : '#8fb4ff', base: '#15171b' };
  return { text: '#262b33', muted: '#5f6773', halo: 'rgba(255, 255, 255, 0.94)', dim: '#0040c8', row: '#0047ab', base: '#ffffff' };
}

const labelText = (lc) => ['case', ['boolean', ['get', 'm'], false], lc.muted, lc.text];

/** "Địa điểm" layer text: group colour on light maps, near-white on dark maps / imagery */
function placeText(basemap) {
  const dark = document.documentElement.dataset.theme === 'dark' || basemap === 'dark' || /imagery|hybrid/.test(basemap);
  if (dark) return { text: '#e8edf3', halo: 'rgba(10, 11, 13, 0.86)', hn: '#c3cad3' };
  return { text: ['match', ['get', 'g'], ...PLACE_GROUPS.flatMap((g) => [g.id, g.color]), '#475569'], halo: 'rgba(255, 255, 255, 0.95)', hn: '#6b7280' };
}
const ATTR_PLACES = 'Địa điểm: © <a href="https://overturemaps.org" target="_blank" rel="noopener">Overture Maps Foundation</a>, © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

/** 16×16 diagonal red hatch (fill-pattern for the planned-road part of a parcel) */
function hatchImage() {
  const n = 16, data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
    const d = (x + y) % 8; // 45° stripes every 8 px
    const o = (y * n + x) * 4;
    if (d < 2) { data[o] = 220; data[o + 1] = 38; data[o + 2] = 38; data[o + 3] = 235; }
    else { data[o] = 254; data[o + 1] = 226; data[o + 2] = 226; data[o + 3] = 150; }
  }
  return { width: n, height: n, data };
}

/** Saved places: amber badge with a white star (canvas, drawn at pixelRatio `px`). */
function savedIcon(px = 2) {
  const S = 21 * px, r = S / 2;
  const c = document.createElement('canvas');
  c.width = c.height = S;
  const x = c.getContext('2d');
  x.beginPath(); x.arc(r, r + 0.5 * px, r - 0.6 * px, 0, Math.PI * 2); x.fillStyle = 'rgba(15, 23, 42, 0.22)'; x.fill();
  x.beginPath(); x.arc(r, r, r - 1.1 * px, 0, Math.PI * 2); x.fillStyle = '#ffffff'; x.fill();
  x.beginPath(); x.arc(r, r, r - 2.6 * px, 0, Math.PI * 2); x.fillStyle = '#f59e0b'; x.fill();
  // five-pointed star with softened corners
  const R = 4.9 * px, ri = 2.05 * px;
  x.beginPath();
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5, rr = i % 2 ? ri : R;
    x[i ? 'lineTo' : 'moveTo'](r + rr * Math.cos(a), r + 0.25 * px + rr * Math.sin(a));
  }
  x.closePath();
  x.fillStyle = '#ffffff'; x.strokeStyle = '#ffffff'; x.lineWidth = 0.9 * px; x.lineJoin = 'round';
  x.fill(); x.stroke();
  return x.getImageData(0, 0, S, S);
}

function savedColors(basemap) {
  const dark = document.documentElement.dataset.theme === 'dark' || basemap === 'dark' || /imagery|hybrid/.test(basemap);
  return dark ? { text: '#fcd34d', halo: 'rgba(10, 11, 13, 0.88)' } : { text: '#92400e', halo: 'rgba(255, 255, 255, 0.95)' };
}

/** "my location": accuracy circle colours */
function gpsColors(basemap) {
  const dark = document.documentElement.dataset.theme === 'dark' || basemap === 'dark';
  const img = /imagery|hybrid/.test(basemap);
  return { fill: img ? '#60a5fa' : dark ? '#6b9bff' : '#2563eb', line: img ? '#93c5fd' : dark ? '#8fb4ff' : '#2563eb' };
}

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/* ---------------- PlanMap ---------------- */

/**
 * WebGL settings. Phones and tablets (touch, no hover) draw at most 2 device pixels per CSS pixel and skip
 * multisampling on dense screens: 3x screens otherwise fill 2.25x the pixels for detail the eye cannot see,
 * which is what makes panning and pinch-zoom stutter on mid-range phones. Off-screen print maps (sheet.js)
 * pass their own pixelRatio and keep full quality.
 * @param {{pixelRatio?:number}} o
 */
export function glOptions(o = {}, env = globalThis) {
  if (o.pixelRatio) return { antialias: true, pixelRatio: o.pixelRatio };
  const dpr = Number(env.devicePixelRatio) || 1;
  let touch = false;
  try { touch = !!env.matchMedia?.('(hover: none) and (pointer: coarse)').matches; } catch { /* no media queries */ }
  if (!touch) return { antialias: true, pixelRatio: null };
  return { antialias: dpr < 2, pixelRatio: dpr > 2 ? 2 : null };
}

export class PlanMap {
  /**
   * @param {HTMLElement} container
   * @param {{view:{lat,lng,zoom,bearing?}, basemap:string, plan:number|null, boundaries:number[], opacity:number,
   *          interactive?:boolean, controls?:boolean, pixelRatio?:number, still?:boolean}} opts
   */
  constructor(container, opts) {
    this.container = container;
    this.basemap = opts.basemap;
    this.ov = { plan: opts.plan, boundaries: [...(opts.boundaries || [])], opacity: opts.opacity ?? 0.82, places: opts.places !== false, saved: opts.saved !== false, gia: !!opts.gia };
    this.data = { hl: EMPTY, proj: EMPTY, approx: EMPTY, pieces: EMPTY, parcel: EMPTY, measure: EMPTY, section: EMPTY, road: EMPTY, labels: EMPTY, row: EMPTY, dims: EMPTY, poi: EMPTY, saved: EMPTY, gps: EMPTY, gia: EMPTY };
    this.dimSrc = { cell: null, parcel: null };
    this.raster = { plan: null, bound: null };
    this.groups = {}; // raster id -> layer ids when one view is several sources (land-use plan maps)
    this.sdd = null; // land-use plan period shown by the 'sdd' view
    this.vsl = null; // source layer of the vector plan in use ('qhpk' / 'qhct'), null: raster or none
    this.vsel = []; // legend highlight: manifest type indices of the picked land-use category
    this.vhover = null; // { sl, id } of the outlined cell under the pointer
    this.gen = 0;
    this.opts = opts;
    this.markers = { pin: null, pois: [], vertices: [] };
    this.ready = this._init();
  }

  async _init() {
    const o = this.opts;
    const vector = loadVectorManifest(); // in parallel with the style; never rejects
    let style;
    try { style = await basemapStyle(this.basemap); }
    catch { style = await imageryStyle(); this.basemap = 'imagery'; }
    await vector;
    const maplibregl = ML();
    registerTransportProtocol(maplibregl);
    registerVectorProtocol(maplibregl);
    const gl = glOptions(o);
    const map = new maplibregl.Map({
      container: this.container,
      style,
      center: [o.view.lng, o.view.lat],
      zoom: o.view.zoom,
      bearing: o.view.bearing || 0,
      minZoom: 8.5,
      maxZoom: 20,
      maxBounds: MAX_BOUNDS,
      maxPitch: 0,
      pitchWithRotate: false,
      touchPitch: false,
      interactive: o.interactive !== false,
      attributionControl: o.controls === false ? false : { compact: true, customAttribution: ATTR_PLAN },
      fadeDuration: o.still ? 0 : 160,
      validateStyle: false,
      cancelPendingTileRequestsWhileZooming: true,
      refreshExpiredTiles: false,
      canvasContextAttributes: { antialias: gl.antialias },
      ...(gl.pixelRatio ? { pixelRatio: gl.pixelRatio } : {}),
    });
    this.map = map;
    // sprites referenced by a style but missing from its sheet (e.g. "wood-pattern"): transparent stub
    map.on('styleimagemissing', (e) => { if (!map.hasImage(e.id)) map.addImage(e.id, { width: 1, height: 1, data: new Uint8Array(4) }); });
    if (o.controls !== false) {
      map.addControl(new maplibregl.ScaleControl({ maxWidth: 110, unit: 'metric' }), 'bottom-right');
    }
    map.on('style.load', () => this._applyOverlays());
    await new Promise((resolve) => map.once('load', resolve));
    return map;
  }

  /* ----- basemap ----- */

  async setBasemap(id) {
    if (id === this.basemap && this.map) return;
    this.basemap = id;
    let style;
    try { style = await basemapStyle(id); }
    catch { return false; }
    if (this.basemap !== id) return false; // superseded
    this.raster = { plan: null, bound: null };
    this.groups = {};
    this.vsl = null;
    this.vhover = null;
    this.map.setStyle(style, { diff: false });
    return true;
  }

  /* ----- overlays ----- */

  _firstSymbol() {
    const layers = this.map.getStyle().layers || [];
    const l = layers.find((x) => x.type === 'symbol' && !x.id.startsWith('qh-'));
    return l ? l.id : undefined;
  }

  _applyOverlays() {
    const m = this.map;
    const before = this._firstSymbol();
    const c = overlayColors(this.basemap);
    // anchors define the stacking slots for the swappable raster layers
    m.addLayer({ id: 'qh-anchor-plan', type: 'background', layout: { visibility: 'none' } }, before);
    m.addLayer({ id: 'qh-anchor-bound', type: 'background', layout: { visibility: 'none' } }, before);
    for (const k of Object.keys(this.data)) m.addSource('qh-' + k, { type: 'geojson', data: this.data[k], tolerance: 0.25, ...(k === 'poi' ? { attribution: ATTR_PLACES } : {}) });

    if (!m.hasImage('qh-hatch')) m.addImage('qh-hatch', hatchImage(), { pixelRatio: 2 });
    const ROADPART = ['==', ['get', 'kind'], 'road'];
    const lc = labelColors(this.basemap);
    // parcel analysis: the part outside every cell of the plan (= planned road) is hatched red;
    // the cells' pieces sit on an opaque base so the hatch only shows in the gaps
    m.addLayer({ id: 'qh-pieces-road', type: 'fill', source: 'qh-pieces', filter: ROADPART, paint: { 'fill-pattern': 'qh-hatch', 'fill-opacity': 0.95 } }, before);
    m.addLayer({ id: 'qh-pieces-road-line', type: 'line', source: 'qh-pieces', filter: ROADPART, paint: { 'line-color': '#dc2626', 'line-width': 1.2, 'line-opacity': 0.0 } }, before);
    m.addLayer({ id: 'qh-pieces-base', type: 'fill', source: 'qh-pieces', filter: ['all', ['!', ROADPART], ['!', ['boolean', ['get', 'dim'], false]], ['!', ['boolean', ['get', 'roadcell'], false]]], paint: { 'fill-color': lc.base, 'fill-opacity': 1, 'fill-antialias': false } }, before);
    m.addLayer({
      id: 'qh-pieces-fill', type: 'fill', source: 'qh-pieces', filter: ['!', ROADPART],
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': ['case', ['boolean', ['get', 'dim'], false], 0.12, 0.66],
        'fill-opacity-transition': { duration: 250 },
        'fill-antialias': false,
      },
    }, before);
    // land price layer (gia.js): road segments coloured by the VT1 residential price
    const gv = this.ov.gia ? 'visible' : 'none';
    const GIA_COLOR = ['step', ['get', 'p'], CLASS_COLORS[0], ...CLASS_STEPS.flatMap((v, i) => [v, CLASS_COLORS[i + 1]])];
    m.addLayer({ id: 'qh-gia-casing', type: 'line', source: 'qh-gia', layout: { visibility: gv, 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': c.casing, 'line-width': ['interpolate', ['linear'], ['zoom'], 11, 2.6, 15, 5.5, 18, 11], 'line-opacity': 0.85 } }, before);
    m.addLayer({ id: 'qh-gia-line', type: 'line', source: 'qh-gia', layout: { visibility: gv, 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': GIA_COLOR, 'line-width': ['interpolate', ['linear'], ['zoom'], 11, 1.5, 15, 3.4, 18, 7.5], 'line-opacity': ['case', ['boolean', ['get', 'amb'], false], 0.62, 0.95] } }, before);
    m.addLayer({ id: 'qh-hl-fill', type: 'fill', source: 'qh-hl', paint: { 'fill-color': c.accent, 'fill-opacity': 0.1 } }, before);

    // approximate extent of a plan known only by its decision (catalog.js): faint fill, dotted outline
    const POLY = ['==', ['geometry-type'], 'Polygon'];
    m.addLayer({ id: 'qh-approx-fill', type: 'fill', source: 'qh-approx', filter: POLY, paint: { 'fill-color': c.approx, 'fill-opacity': 0.07 } }, before);
    m.addLayer({ id: 'qh-approx-line', type: 'line', source: 'qh-approx', filter: POLY, layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': c.approx, 'line-width': 2.2, 'line-dasharray': [0.6, 2.2], 'line-opacity': 0.95 } });
    m.addLayer({ id: 'qh-proj-line', type: 'line', source: 'qh-proj', layout: { 'line-join': 'round' }, paint: { 'line-color': c.project, 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 1.4, 16, 2.4], 'line-dasharray': [2, 1.5], 'line-opacity': 0.95 } });
    m.addLayer({ id: 'qh-hl-casing', type: 'line', source: 'qh-hl', layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': c.casing, 'line-width': ['interpolate', ['linear'], ['zoom'], 12, 3.5, 18, 6.5], 'line-opacity': 1, 'line-opacity-transition': { duration: 350 } } });
    m.addLayer({ id: 'qh-hl-line', type: 'line', source: 'qh-hl', layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': c.accent, 'line-width': ['interpolate', ['linear'], ['zoom'], 12, 1.8, 18, 3.2], 'line-opacity': 1, 'line-opacity-transition': { duration: 350 } } });

    m.addLayer({ id: 'qh-parcel-fill', type: 'fill', source: 'qh-parcel', filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'fill-color': c.accent, 'fill-opacity': ['case', ['boolean', ['get', 'analysed'], false], 0, 0.14] } });
    m.addLayer({ id: 'qh-parcel-casing', type: 'line', source: 'qh-parcel', filter: OUTLINE, layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': c.casing, 'line-width': 5.5 } });
    m.addLayer({ id: 'qh-parcel-line', type: 'line', source: 'qh-parcel', filter: OUTLINE, layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': c.accent, 'line-width': 2.6, 'line-dasharray': ['case', ['boolean', ['get', 'draft'], false], ['literal', [2, 1.4]], ['literal', [1, 0]]] } });

    m.addLayer({ id: 'qh-measure-fill', type: 'fill', source: 'qh-measure', filter: ['==', ['geometry-type'], 'Polygon'], paint: { 'fill-color': c.measure, 'fill-opacity': 0.16 } });
    m.addLayer({ id: 'qh-measure-casing', type: 'line', source: 'qh-measure', filter: OUTLINE, layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': c.casing, 'line-width': 5 } });
    m.addLayer({ id: 'qh-measure-line', type: 'line', source: 'qh-measure', filter: OUTLINE, layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': c.measure, 'line-width': 2.4, 'line-dasharray': ['case', ['boolean', ['get', 'preview'], false], ['literal', [1.5, 1.5]], ['literal', [1, 0]]] } });
    m.addLayer({ id: 'qh-measure-pts', type: 'circle', source: 'qh-measure', filter: ['==', ['geometry-type'], 'Point'], paint: { 'circle-radius': ['case', ['boolean', ['get', 'first'], false], 6, 4.5], 'circle-color': '#ffffff', 'circle-stroke-color': c.measure, 'circle-stroke-width': 2.4 } });
    // road cross-section (estimated right-of-way between the two facing cells)
    m.addLayer({ id: 'qh-section-casing', type: 'line', source: 'qh-section', filter: ['==', ['geometry-type'], 'LineString'], layout: { 'line-cap': 'round' }, paint: { 'line-color': c.casing, 'line-width': 6 } });
    m.addLayer({ id: 'qh-section-line', type: 'line', source: 'qh-section', filter: ['==', ['geometry-type'], 'LineString'], layout: { 'line-cap': 'round' }, paint: { 'line-color': c.measure, 'line-width': 2.8 } });
    m.addLayer({ id: 'qh-section-ends', type: 'circle', source: 'qh-section', filter: ['==', ['geometry-type'], 'Point'], paint: { 'circle-radius': 4.5, 'circle-color': '#ffffff', 'circle-stroke-color': c.measure, 'circle-stroke-width': 2.4 } });
    // road-planning analysis (Giao thông tab): right-of-way band, buildings inside it, red lines, centreline by status
    const K = (k) => ['==', ['get', 'kind'], k];
    m.addLayer({ id: 'qh-road-band', type: 'fill', source: 'qh-road', filter: K('band'), paint: { 'fill-color': '#ef4444', 'fill-opacity': 0.1, 'fill-antialias': false } }, before);
    m.addLayer({ id: 'qh-road-bld', type: 'fill', source: 'qh-road', filter: K('building'), paint: { 'fill-color': '#dc2626', 'fill-opacity': ['case', ['get', 'sig'], 0.62, 0.32] } }, before);
    m.addLayer({ id: 'qh-road-bld-line', type: 'line', source: 'qh-road', filter: K('building'), paint: { 'line-color': '#991b1b', 'line-width': ['interpolate', ['linear'], ['zoom'], 15, 0.5, 19, 1.4] } }, before);
    m.addLayer({ id: 'qh-road-red', type: 'line', source: 'qh-road', filter: K('redline'), layout: { 'line-join': 'round' }, paint: { 'line-color': '#dc2626', 'line-width': ['interpolate', ['linear'], ['zoom'], 14, 1.2, 18, 2.6], 'line-dasharray': [3, 1.6] } });
    m.addLayer({ id: 'qh-road-route-casing', type: 'line', source: 'qh-road', filter: K('route'), layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': '#ffffff', 'line-width': ['interpolate', ['linear'], ['zoom'], 14, 4, 18, 8], 'line-opacity': 0.9 } });
    m.addLayer({ id: 'qh-road-route', type: 'line', source: 'qh-road', filter: K('route'), layout: { 'line-join': 'round', 'line-cap': 'round' }, paint: { 'line-color': ['match', ['get', 'status'], 'keep', '#16a34a', 'widen', '#f59e0b', 'incell', '#8b5cf6', 'new', '#2563eb', 'open', '#94a3b8', '#cbd5e1'], 'line-width': ['interpolate', ['linear'], ['zoom'], 14, 2.2, 18, 4.6] } });

    // "my location" accuracy circle (gps.js draws the dot itself as an HTML marker)
    const gc = gpsColors(this.basemap);
    m.addLayer({ id: 'qh-gps-acc', type: 'fill', source: 'qh-gps', paint: { 'fill-color': gc.fill, 'fill-opacity': 0.1, 'fill-antialias': false } }, before);
    m.addLayer({ id: 'qh-gps-acc-line', type: 'line', source: 'qh-gps', paint: { 'line-color': gc.line, 'line-width': 1, 'line-opacity': 0.45 } }, before);

    // places (places.js) and house numbers: below the basemap's own labels so street / place
    // names keep priority, above the planning raster
    for (const g of PLACE_GROUPS) {
      const id = 'qh-poi-' + g.id;
      if (m.hasImage(id)) m.removeImage(id);
      try { m.addImage(id, placeIcon(g, 2), { pixelRatio: 2 }); } catch { /* canvas unavailable */ }
    }
    const pt = placeText(this.basemap);
    let omt = 'openmaptiles';
    if (!m.getSource(omt) || m.getSource(omt).type !== 'vector') {
      omt = 'qh-omt';
      if (!m.getSource(omt)) m.addSource(omt, { type: 'vector', url: OMT_URL, attribution: '' });
    }
    m.addLayer({
      id: 'qh-housenumber', type: 'symbol', source: omt, 'source-layer': 'housenumber', minzoom: 17,
      layout: { 'text-field': ['to-string', ['get', 'housenumber']], 'text-font': ['Noto Sans Regular'], 'text-size': ['interpolate', ['linear'], ['zoom'], 17, 9.5, 19, 11], 'text-padding': 1, 'text-max-width': 6 },
      paint: { 'text-color': pt.hn, 'text-halo-color': pt.halo, 'text-halo-width': 1.2 },
    }, before);
    m.addLayer({
      id: 'qh-poi', type: 'symbol', source: 'qh-poi',
      filter: ['>=', ['zoom'], ['get', 'mz']],
      layout: {
        'icon-image': ['concat', 'qh-poi-', ['get', 'g']],
        'icon-size': ['case', ['<=', ['get', 'mz'], 16], 1, 0.88],
        'icon-padding': 1,
        'text-field': ['get', 'n'],
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 15, ['case', ['<=', ['get', 'mz'], 15], 11, 10.5], 19, 12],
        'text-variable-anchor': ['left', 'right', 'top', 'bottom'],
        'text-radial-offset': 0.95,
        'text-justify': 'auto',
        'text-max-width': 8,
        'text-line-height': 1.1,
        'text-padding': 2,
        'text-optional': true,
        'symbol-sort-key': ['get', 'mz'],
      },
      paint: { 'text-color': pt.text, 'text-halo-color': pt.halo, 'text-halo-width': 1.3, 'icon-opacity': 0.98 },
    }, before);

    // right-of-way widths along roads (transport view, rowlabels.js)
    m.addLayer({
      id: 'qh-row-label', type: 'symbol', source: 'qh-row', minzoom: 15.5,
      layout: {
        'text-field': ['get', 't'],
        'text-font': ['Noto Sans Bold'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 16, 10.5, 19, 12.5],
        'text-rotate': ['get', 'rot'],
        'text-rotation-alignment': 'map',
        'text-pitch-alignment': 'viewport',
        'text-offset': [0, 1.05],
        'text-padding': 4,
        'symbol-sort-key': ['-', 0, ['get', 'len']],
      },
      paint: { 'text-color': lc.row, 'text-halo-color': lc.halo, 'text-halo-width': 1.6 },
    });
    // land-use names (labels.js) and dimension labels (dims.js) above everything else
    m.addLayer({
      id: 'qh-label', type: 'symbol', source: 'qh-labels',
      filter: ['>=', ['zoom'], ['get', 'mz']],
      layout: {
        'text-field': ['step', ['zoom'], ['get', 't'], 17, ['format', ['get', 't'], {}, ['case', ['==', ['get', 'a'], ''], '', '\n'], {}, ['get', 'a'], { 'font-scale': 0.86 }]],
        'text-font': ['Noto Sans Regular'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 12, ['case', ['boolean', ['get', 'm'], false], 9.5, 10.5], 17, ['case', ['boolean', ['get', 'm'], false], 10.5, 12], 19, ['case', ['boolean', ['get', 'm'], false], 12, 13.5]],
        'text-max-width': 7.5,
        'text-line-height': 1.15,
        'text-padding': 3,
        'symbol-sort-key': ['-', 0, ['get', 'r']],
        'text-optional': false,
      },
      paint: { 'text-color': labelText(lc), 'text-halo-color': lc.halo, 'text-halo-width': 1.4, 'text-halo-blur': 0.3, 'text-opacity': ['case', ['boolean', ['get', 'm'], false], 0.9, 1], 'text-opacity-transition': { duration: 200 } },
    });
    const DK = (k) => ['==', ['get', 'k'], k];
    m.addLayer({
      id: 'qh-dims-edge', type: 'symbol', source: 'qh-dims', filter: ['any', ['all', DK('cell'), ['>=', ['zoom'], 16]], DK('parcel')],
      layout: { 'symbol-placement': 'line-center', 'text-field': ['get', 't'], 'text-font': ['Noto Sans Bold'], 'text-size': ['interpolate', ['linear'], ['zoom'], 16, 10.5, 19, 12.5], 'text-keep-upright': true, 'text-allow-overlap': false, 'text-padding': 1, 'symbol-sort-key': ['-', 0, ['get', 'len']] },
      paint: { 'text-color': lc.dim, 'text-halo-color': lc.halo, 'text-halo-width': 1.8 },
    });
    m.addLayer({
      id: 'qh-dims-area', type: 'symbol', source: 'qh-dims', filter: ['all', DK('cell-area'), ['>=', ['zoom'], 15]],
      layout: { 'text-field': ['get', 't'], 'text-font': ['Noto Sans Bold'], 'text-size': ['interpolate', ['linear'], ['zoom'], 15, 12, 19, 15], 'text-offset': [0, 1.35], 'text-allow-overlap': true, 'text-ignore-placement': false },
      paint: { 'text-color': lc.dim, 'text-halo-color': lc.halo, 'text-halo-width': 2 },
    });
    m.addLayer({
      id: 'qh-approx-label', type: 'symbol', source: 'qh-approx', filter: ['==', ['geometry-type'], 'Point'],
      layout: {
        'text-field': ['format', ['get', 't'], {}, '\n', {}, ['get', 's'], { 'font-scale': 0.78, 'text-font': ['literal', ['Noto Sans Regular']] }],
        'text-font': ['Noto Sans Bold'], 'text-size': 14, 'text-max-width': 16, 'text-line-height': 1.3,
        'text-allow-overlap': true, 'text-ignore-placement': true,
      },
      paint: { 'text-color': c.approx, 'text-halo-color': lc.halo, 'text-halo-width': 2 },
    });

    // saved places (saved.js): always on top, names from z13
    if (m.hasImage('qh-saved-icon')) m.removeImage('qh-saved-icon');
    try { m.addImage('qh-saved-icon', savedIcon(2), { pixelRatio: 2 }); } catch { /* canvas unavailable */ }
    const sc = savedColors(this.basemap);
    m.addLayer({
      id: 'qh-saved', type: 'symbol', source: 'qh-saved',
      layout: {
        visibility: this.ov.saved ? 'visible' : 'none',
        'icon-image': 'qh-saved-icon',
        'icon-size': ['interpolate', ['linear'], ['zoom'], 10, 0.74, 14, 0.9, 17, 1],
        'icon-allow-overlap': true,
        'icon-padding': 0,
        'text-field': ['step', ['zoom'], '', 13, ['get', 'n']],
        'text-font': ['Noto Sans Bold'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 13, 10.5, 18, 12.5],
        'text-anchor': 'top',
        'text-offset': [0, 0.95],
        'text-max-width': 9,
        'text-line-height': 1.1,
        'text-padding': 2,
        'text-optional': true,
        'symbol-sort-key': ['get', 'o'],
      },
      paint: { 'text-color': sc.text, 'text-halo-color': sc.halo, 'text-halo-width': 1.6 },
    });
    m.addLayer({
      id: 'qh-gia-label', type: 'symbol', source: 'qh-gia', minzoom: 15,
      layout: {
        visibility: gv, 'symbol-placement': 'line', 'symbol-spacing': 360, 'text-field': ['get', 'n'], 'text-font': ['Noto Sans Bold'],
        'text-size': ['interpolate', ['linear'], ['zoom'], 15, 10.5, 18, 12.5], 'text-max-angle': 35, 'text-padding': 6, 'text-keep-upright': true,
        'symbol-sort-key': ['-', 0, ['get', 'p']],
      },
      paint: { 'text-color': lc.text, 'text-halo-color': lc.halo, 'text-halo-width': 1.8 },
    }, 'qh-saved');

    this._addVectorPlan(c, lc);
    this._swapRaster('plan', this.ov.plan ? [this.ov.plan] : [], true);
    this._swapRaster('bound', this.ov.boundaries, true);
    this._syncTransport();
    this._syncPlaces();
  }

  /* ----- vector QH 1/2000 (public/data/qhv, core/vplan.js) ----- */

  /**
   * One vector source, per source layer: the fills in the raster slot (official colours, 40 % alpha,
   * small cells drawn over big ones like the raster), the legend-highlight outline and the hover
   * outline (feature-state, z14 tiles carry ids) under the selection overlays. All hidden until
   * _swapRaster picks a vector plan.
   */
  _addVectorPlan(c, lc) {
    const m = this.map, vm = vectorManifest();
    if (!vm || m.getSource('qh-v')) return;
    m.addSource('qh-v', { type: 'vector', tiles: ['qhv://{z}/{x}/{y}'], minzoom: vm.minzoom, maxzoom: vm.maxzoom, bounds: vm.bounds || HANOI_BBOX, attribution: ATTR_PLAN });
    const HOVER = ['boolean', ['feature-state', 'hover'], false];
    const hidden = { visibility: 'none', 'line-join': 'round' };
    for (const [plan, sl] of Object.entries(VLAYER)) {
      if (!vm.layers[sl]) continue;
      const id = 'qh-v-' + sl;
      m.addLayer({
        id, type: 'fill', source: 'qh-v', 'source-layer': sl, layout: { visibility: 'none' },
        paint: { 'fill-color': fillColorExpr(vm, +plan), 'fill-opacity': 0, 'fill-opacity-transition': { duration: 380 } },
      }, 'qh-anchor-plan');
      // legend highlight: the picked types again, solid, over the faded layer, plus a thin outline
      m.addLayer({
        id: id + '-pick', type: 'fill', source: 'qh-v', 'source-layer': sl, filter: ['in', ['get', 't'], ['literal', []]], layout: { visibility: 'none' },
        paint: { 'fill-color': fillColorExpr(vm, +plan, true), 'fill-opacity': 0.6 },
      }, 'qh-anchor-plan');
      m.addLayer({
        id: id + '-sel', type: 'line', source: 'qh-v', 'source-layer': sl, filter: ['in', ['get', 't'], ['literal', []]], layout: hidden,
        paint: { 'line-color': lc.text, 'line-opacity': 0.55, 'line-width': ['interpolate', ['linear'], ['zoom'], 10, 0.35, 14, 0.9, 18, 1.8] },
      }, 'qh-pieces-road');
      m.addLayer({
        id: id + '-hc', type: 'line', source: 'qh-v', 'source-layer': sl, minzoom: 14, layout: hidden,
        paint: { 'line-color': c.casing, 'line-width': 4.6, 'line-opacity': ['case', HOVER, 0.9, 0] },
      }, 'qh-pieces-road');
      m.addLayer({
        id: id + '-hl', type: 'line', source: 'qh-v', 'source-layer': sl, minzoom: 14, layout: hidden,
        paint: { 'line-color': lc.text, 'line-width': 2, 'line-opacity': ['case', HOVER, 1, 0] },
      }, 'qh-pieces-road');
    }
  }

  /** Source layer serving `plan` as vectors on this map, or null (raster). */
  _vLayer(plan) {
    const sl = vectorLayerOf(vectorManifest(), plan);
    return sl && this.map?.getLayer('qh-v-' + sl) ? sl : null;
  }

  /** Fill opacity / highlight outline of the active vector layer. */
  _vectorPaint(instant = false) {
    const m = this.map, sl = this.vsl;
    if (!m || !sl || !m.getLayer('qh-v-' + sl)) return;
    const id = 'qh-v-' + sl;
    const o = vectorOpacity(this.ov.opacity, this.vsel);
    if (instant) m.setPaintProperty(id, 'fill-opacity-transition', { duration: 0 });
    m.setPaintProperty(id, 'fill-opacity', o.base);
    if (instant) m.setPaintProperty(id, 'fill-opacity-transition', { duration: 380 });
    const f = ['in', ['get', 't'], ['literal', [...this.vsel]]];
    const vis = this.vsel.length ? 'visible' : 'none';
    for (const x of ['-pick', '-sel']) { m.setFilter(id + x, f); m.setLayoutProperty(id + x, 'visibility', vis); }
    m.setPaintProperty(id + '-pick', 'fill-opacity', o.pick || 0.6);
  }

  /** Show the fills of `sl` (null: none) and fade the other vector layers out. */
  _vectorVisual(sl, instant = false) {
    const m = this.map;
    for (const s of Object.values(VLAYER)) {
      const id = 'qh-v-' + s;
      if (!m.getLayer(id)) continue;
      const on = s === sl;
      for (const x of ['-hc', '-hl']) m.setLayoutProperty(id + x, 'visibility', on ? 'visible' : 'none');
      if (on) {
        if (m.getLayoutProperty(id, 'visibility') === 'none') {
          m.setPaintProperty(id, 'fill-opacity-transition', { duration: 0 });
          m.setPaintProperty(id, 'fill-opacity', 0);
          m.setPaintProperty(id, 'fill-opacity-transition', { duration: 380 });
          m.setLayoutProperty(id, 'visibility', 'visible');
        }
        this._vectorPaint(instant || reducedMotion());
        continue;
      }
      for (const x of ['-pick', '-sel']) m.setLayoutProperty(id + x, 'visibility', 'none');
      if (m.getLayoutProperty(id, 'visibility') === 'none') continue;
      const hide = () => { if (this.vsl !== s && m.getLayer(id)) m.setLayoutProperty(id, 'visibility', 'none'); };
      if (instant || reducedMotion()) { hide(); continue; }
      m.setPaintProperty(id, 'fill-opacity', 0);
      setTimeout(hide, 420);
    }
  }

  /** The vector plan in use: { plan, sl } or null when the plan is a raster (or off). */
  vectorPlan() { return this.vsl ? { plan: this.ov.plan, sl: this.vsl } : null; }

  /**
   * Topmost cell of the vector plan under a screen point (small cells are drawn last, so they come
   * first): { id, props, type, plan } or null.
   */
  cellAt(point) {
    const m = this.map, sl = this.vsl;
    if (!m || !sl || !m.getLayer('qh-v-' + sl)) return null;
    const f = m.queryRenderedFeatures(point, { layers: ['qh-v-' + sl] })[0];
    if (!f) return null;
    const p = f.properties || {};
    return { id: f.id ?? null, props: p, type: vectorManifest()?.types?.[p.t] || null, plan: this.ov.plan };
  }

  /** Outline the cell with this feature id (z14 tiles) under the pointer; null clears it. */
  hoverCell(id) {
    const m = this.map, h = this.vhover;
    if (h && h.id === id && h.sl === this.vsl) return;
    if (h && m?.getSource('qh-v')) m.setFeatureState({ source: 'qh-v', sourceLayer: h.sl, id: h.id }, { hover: false });
    this.vhover = null;
    if (id == null || !this.vsl || !m?.getSource('qh-v')) return;
    m.setFeatureState({ source: 'qh-v', sourceLayer: this.vsl, id }, { hover: true });
    this.vhover = { sl: this.vsl, id };
  }

  /** Legend highlight: keep these land-use types (manifest indices) and fade the others; [] clears. */
  highlightTypes(ts) {
    const next = [...new Set(ts || [])].sort((a, b) => a - b);
    if (next.join() === this.vsel.join()) return;
    this.vsel = next;
    this._vectorPaint(true);
  }

  /** Show / hide the saved-places layer. */
  setSavedVisible(on) {
    this.ov.saved = !!on;
    if (this.map?.getLayer('qh-saved')) this.map.setLayoutProperty('qh-saved', 'visibility', on ? 'visible' : 'none');
  }

  /** Show / hide the land price layer (gia.js feeds its data). */
  setGiaVisible(on) {
    this.ov.gia = !!on;
    for (const id of ['qh-gia-casing', 'qh-gia-line', 'qh-gia-label']) if (this.map?.getLayer(id)) this.map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
  }

  /** Our places layer replaces the basemap's own POI layers (fewer, OSM only) when switched on. */
  setPlaces(on) {
    this.ov.places = !!on;
    this._syncPlaces();
  }

  _syncPlaces() {
    const m = this.map;
    if (!m || !m.getLayer('qh-poi')) return;
    const on = this.ov.places;
    for (const l of m.getStyle().layers || []) {
      if (l.type !== 'symbol') continue;
      if (l.id === 'qh-poi' || l.id === 'qh-housenumber') m.setLayoutProperty(l.id, 'visibility', on ? 'visible' : 'none');
      else if (!l.id.startsWith('qh-') && /^poi([_-]|$)/.test(l.id)) m.setLayoutProperty(l.id, 'visibility', on ? 'none' : 'visible');
    }
  }

  /** OpenStreetMap rail / metro / construction overlay shown with the transport view. */
  _syncTransport(recolor = false) {
    const m = this.map;
    if (!m || !m.getLayer('qh-anchor-bound')) return;
    const on = this.ov.plan === GT;
    const present = !!m.getLayer('qh-gt-metro');
    if (present && (!on || recolor)) {
      for (const l of [...m.getStyle().layers].reverse()) if (l.id.startsWith('qh-gt-')) m.removeLayer(l.id);
    }
    if (!on) return;
    if (present && !recolor) return;
    let src = 'openmaptiles';
    if (!m.getSource(src) || m.getSource(src).type !== 'vector') {
      src = 'qh-omt';
      if (!m.getSource(src)) m.addSource(src, { type: 'vector', url: OMT_URL, attribution: '' });
    }
    const theme = document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
    const c = transportColors(theme, /imagery|hybrid/.test(this.basemap));
    for (const l of transportLayers(src, c)) m.addLayer(l, 'qh-anchor-bound');
    const top = m.getLayer('qh-proj-line') ? 'qh-proj-line' : undefined;
    for (const l of transportSymbols(src, c)) m.addLayer(l, top);
  }

  _rasterSource(layers) {
    if (layers[0] === GT) {
      this.gtTheme = document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
      return {
        type: 'raster',
        tiles: [transportTiles(this.gtTheme)],
        tileSize: 512,
        minzoom: 11,
        maxzoom: 18,
        bounds: HANOI_BBOX,
        attribution: ATTR_PLAN,
      };
    }
    return {
      type: 'raster',
      tiles: [`${ORIGIN}/api/tile/${layers.join(',')}/{z}/{x}/{y}.png`],
      tileSize: 512,
      minzoom: 9,
      maxzoom: 18,
      bounds: HANOI_BBOX,
      attribution: ATTR_PLAN,
    };
  }

  /**
   * Replace a planning raster with a cross-fade once the new tiles are in. Plans with vector tiles
   * (core/vplan.js) use the vector fills in the same slot instead.
   */
  _swapRaster(kind, layers, instant = false) {
    const m = this.map;
    if (!m || !m.getLayer('qh-anchor-' + kind)) return;
    const old = this.raster[kind];
    const opacity = kind === 'plan' ? this.ov.opacity : 1;
    const sdd = layers[0] === SDD;
    const none = !layers.length || (sdd && !this.sdd?.layers?.length);
    // the vector fills sit under any raster of the slot: they appear at once and the old raster
    // fades out once the vector tiles are in; going back to a raster, they go when it is in
    const sl = kind === 'plan' && layers.length === 1 ? this._vLayer(layers[0]) : null;
    if (kind === 'plan') {
      this.vsl = sl;
      if (!sl) this.hoverCell(null);
      if (sl || none || instant) this._vectorVisual(sl, instant);
    }
    if (sl || none) {
      this.raster[kind] = null;
      if (!old) return;
      if (!sl || instant) { this._fadeOutRemove(old, instant); return; }
      let done = false;
      const fin = () => { if (done) return; done = true; m.off('sourcedata', onV); this._fadeOutRemove(old); };
      const onV = (e) => { if (e.sourceId === 'qh-v' && e.tile && m.isSourceLoaded('qh-v')) fin(); };
      m.on('sourcedata', onV);
      setTimeout(fin, 1500);
      return;
    }
    const id = `qh-${kind}-${++this.gen}`;
    const paint = () => ({ 'raster-opacity': instant ? opacity : 0, 'raster-opacity-transition': { duration: 380 }, 'raster-fade-duration': 220, 'raster-resampling': 'linear' });
    let ids;
    if (sdd) {
      // land-use plan maps: one clipped raster per former district, each limited to its bounds
      ids = this.sdd.layers.map(([key, , b, min, max], i) => {
        const sid = `${id}-${i}`;
        m.addSource(sid, { type: 'raster', tiles: [`${ORIGIN}/api/sdd/${key}/{z}/{x}/{y}.png`], tileSize: 256, minzoom: Math.max(9, min), maxzoom: Math.min(18, max), bounds: b, attribution: ATTR_SDD });
        m.addLayer({ id: sid, type: 'raster', source: sid, paint: paint() }, 'qh-anchor-' + kind);
        return sid;
      });
      this.groups[id] = ids;
    } else {
      m.addSource(id, this._rasterSource(layers[0] === GT ? layers : [...layers].sort((a, b) => a - b)));
      m.addLayer({ id, type: 'raster', source: id, paint: paint() }, 'qh-anchor-' + kind);
      ids = [id];
    }
    this.raster[kind] = id;
    if (instant) { if (old) this._fadeOutRemove(old, true); return; }
    let done = false;
    const finish = () => {
      if (done) return; done = true;
      m.off('sourcedata', onData);
      if (!ids.some((x) => m.getLayer(x))) return;
      const on = this.raster[kind] === id;
      for (const x of ids) if (m.getLayer(x)) m.setPaintProperty(x, 'raster-opacity', on ? opacity : 0);
      if (old) this._fadeOutRemove(old);
      if (kind === 'plan' && on && !this.vsl) this._vectorVisual(null);
    };
    const onData = (e) => { if (ids.includes(e.sourceId) && m.isSourceLoaded(e.sourceId)) finish(); };
    m.on('sourcedata', onData);
    setTimeout(finish, 2200);
  }

  _fadeOutRemove(id, instant = false) {
    const m = this.map;
    const ids = this.groups[id] || [id];
    delete this.groups[id];
    if (!ids.some((x) => m.getLayer(x))) return;
    const rm = () => { for (const x of ids) { if (m.getLayer(x)) m.removeLayer(x); if (m.getSource(x)) m.removeSource(x); } };
    if (instant || reducedMotion()) return rm();
    for (const x of ids) if (m.getLayer(x)) m.setPaintProperty(x, 'raster-opacity', 0);
    setTimeout(rm, 420);
  }

  /** Land-use plan period of the 'sdd' view (catalogue entry from public/data/khsdd.json). */
  setSdd(period) {
    if (period === this.sdd) return;
    this.sdd = period || null;
    // no isStyleLoaded() guard: it is false while any tile loads; _swapRaster checks the anchors
    if (this.ov.plan === SDD && this.map) this._swapRaster('plan', [SDD]);
  }

  setPlan(plan) {
    if (plan === this.ov.plan) return;
    this.ov.plan = plan;
    if (this.map) {
      this._swapRaster('plan', plan ? [plan] : []);
      this._syncTransport();
    }
  }

  setBoundaries(list) {
    const next = [...new Set(list)].sort((a, b) => a - b);
    if (next.join() === this.ov.boundaries.join()) return;
    this.ov.boundaries = next;
    this._swapRaster('bound', next);
  }

  setOpacity(v) {
    this.ov.opacity = v;
    this._vectorPaint(true);
    const id = this.raster.plan;
    for (const x of id ? this.groups[id] || [id] : []) {
      if (!this.map.getLayer(x)) continue;
      this.map.setPaintProperty(x, 'raster-opacity-transition', { duration: 0 });
      this.map.setPaintProperty(x, 'raster-opacity', v);
      this.map.setPaintProperty(x, 'raster-opacity-transition', { duration: 380 });
    }
  }

  refreshColors() {
    const m = this.map;
    if (!m || !m.getLayer('qh-hl-line')) return;
    const c = overlayColors(this.basemap);
    const set = (id, p, v) => m.getLayer(id) && m.setPaintProperty(id, p, v);
    set('qh-hl-fill', 'fill-color', c.accent);
    set('qh-hl-line', 'line-color', c.accent);
    set('qh-hl-casing', 'line-color', c.casing);
    set('qh-gia-casing', 'line-color', c.casing);
    set('qh-proj-line', 'line-color', c.project);
    set('qh-approx-fill', 'fill-color', c.approx);
    set('qh-approx-line', 'line-color', c.approx);
    set('qh-approx-label', 'text-color', c.approx);
    set('qh-parcel-fill', 'fill-color', c.accent);
    set('qh-parcel-line', 'line-color', c.accent);
    set('qh-parcel-casing', 'line-color', c.casing);
    for (const id of ['qh-measure-fill']) set(id, 'fill-color', c.measure);
    set('qh-measure-line', 'line-color', c.measure);
    set('qh-measure-casing', 'line-color', c.casing);
    set('qh-measure-pts', 'circle-stroke-color', c.measure);
    set('qh-section-line', 'line-color', c.measure);
    set('qh-section-casing', 'line-color', c.casing);
    set('qh-section-ends', 'circle-stroke-color', c.measure);
    const lc = labelColors(this.basemap);
    set('qh-label', 'text-color', labelText(lc));
    set('qh-label', 'text-halo-color', lc.halo);
    for (const id of ['qh-dims-edge', 'qh-dims-area']) { set(id, 'text-color', lc.dim); set(id, 'text-halo-color', lc.halo); }
    set('qh-row-label', 'text-color', lc.row);
    set('qh-row-label', 'text-halo-color', lc.halo);
    set('qh-approx-label', 'text-halo-color', lc.halo);
    set('qh-gia-label', 'text-color', lc.text);
    set('qh-gia-label', 'text-halo-color', lc.halo);
    set('qh-pieces-base', 'fill-color', lc.base);
    const pt = placeText(this.basemap);
    set('qh-poi', 'text-color', pt.text);
    set('qh-poi', 'text-halo-color', pt.halo);
    set('qh-housenumber', 'text-color', pt.hn);
    set('qh-housenumber', 'text-halo-color', pt.halo);
    const sc = savedColors(this.basemap);
    set('qh-saved', 'text-color', sc.text);
    set('qh-saved', 'text-halo-color', sc.halo);
    const gc = gpsColors(this.basemap);
    set('qh-gps-acc', 'fill-color', gc.fill);
    set('qh-gps-acc-line', 'line-color', gc.line);
    for (const sl of Object.values(VLAYER)) {
      set(`qh-v-${sl}-sel`, 'line-color', lc.text);
      set(`qh-v-${sl}-hc`, 'line-color', c.casing);
      set(`qh-v-${sl}-hl`, 'line-color', lc.text);
    }
    if (this.ov.plan === GT) {
      this._syncTransport(true);
      const theme = document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
      if (theme !== this.gtTheme) this._swapRaster('plan', [GT]);
    }
  }

  setData(key, fc) {
    if (key === 'parcel') {
      const f = fc?.features?.find((x) => x.geometry?.type === 'Polygon' && x.properties?.analysed);
      this.dimSrc.parcel = f ? f.geometry.coordinates[0] : null;
      this._syncDims();
    }
    this.data[key] = fc || EMPTY;
    const src = this.map?.getSource('qh-' + key);
    if (src) src.setData(this.data[key]);
  }

  /** Highlight a planning cell (with a soft fade-in) and optionally its project boundary. */
  _syncDims() {
    let fc = EMPTY;
    try { fc = buildDims(this.dimSrc.cell, this.dimSrc.parcel); } catch { /* dimension labels are cosmetic */ }
    this.data.dims = fc;
    this.map?.getSource('qh-dims')?.setData(fc);
  }

  highlight(cellGeom, projectGeom) {
    const m = this.map;
    this.dimSrc.cell = cellGeom || null;
    this._syncDims();
    const fc = cellGeom ? { type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: cellGeom }] } : EMPTY;
    const pc = projectGeom ? { type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: projectGeom }] } : EMPTY;
    if (m.getLayer('qh-hl-line') && cellGeom && !reducedMotion()) {
      for (const id of ['qh-hl-line', 'qh-hl-casing']) {
        m.setPaintProperty(id, 'line-opacity-transition', { duration: 0 });
        m.setPaintProperty(id, 'line-opacity', 0);
      }
      this.setData('hl', fc);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        for (const id of ['qh-hl-line', 'qh-hl-casing']) {
          if (!m.getLayer(id)) continue;
          m.setPaintProperty(id, 'line-opacity-transition', { duration: 420 });
          m.setPaintProperty(id, 'line-opacity', 1);
        }
      }));
    } else this.setData('hl', fc);
    this.setData('proj', pc);
  }

  /**
   * Approximate extent of a plan that is only known by its decision: a dotted rectangle with a
   * label, deliberately unlike the dashed boundary of digitised projects. `null` clears it.
   */
  setApprox(bb, { title = '', sub = '' } = {}) {
    if (!bb) { this.setData('approx', EMPTY); return; }
    const [w, s, e, n] = bb;
    this.setData('approx', {
      type: 'FeatureCollection',
      features: [
        { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [[[w, s], [e, s], [e, n], [w, n], [w, s]]] } },
        { type: 'Feature', properties: { t: title, s: sub }, geometry: { type: 'Point', coordinates: [(w + e) / 2, (s + n) / 2] } },
      ],
    });
  }

  /* ----- markers ----- */

  setPin(ll) {
    if (this.markers.pin) { this.markers.pin.remove(); this.markers.pin = null; }
    if (!ll) return;
    const el = document.createElement('div');
    el.className = 'pin';
    el.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><use href="/assets/b/icons-d8baf343d1.svg#i-map-pin"/></svg><span class="pulse"></span>';
    this.markers.pin = new (ML().Marker)({ element: el, anchor: 'bottom', offset: [0, 11] }).setLngLat([ll.lng, ll.lat]).addTo(this.map);
  }

  /** Small HTML tag pinned on the map (e.g. the estimated road width). */
  setTag(ll, html, cls = '') {
    if (this.markers.tag) { this.markers.tag.remove(); this.markers.tag = null; }
    if (!ll) return;
    const el = document.createElement('div');
    el.className = 'm-label tag' + (cls ? ' ' + cls : '');
    el.innerHTML = html;
    this.markers.tag = new (ML().Marker)({ element: el, anchor: 'bottom', offset: [0, -10] }).setLngLat([ll.lng, ll.lat]).addTo(this.map);
  }

  setPois(items, onClick) {
    for (const mk of this.markers.pois) mk.remove();
    this.markers.pois = [];
    (items || []).forEach((it, i) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'poi-marker';
      el.style.setProperty('--i', i);
      el.setAttribute('aria-label', it.name);
      el.setAttribute('data-tip', it.name);
      el.innerHTML = icon(it.icon || 'map-pin');
      el.addEventListener('click', (e) => { e.stopPropagation(); onClick?.(it); });
      this.markers.pois.push(new (ML().Marker)({ element: el }).setLngLat([it.lng, it.lat]).addTo(this.map));
    });
  }

  /* ----- camera ----- */

  /** Ease when the target is already in view, fly otherwise (smooth, bounded duration). */
  goTo({ lat, lng }, { zoom, padding } = {}) {
    const m = this.map;
    const z = zoom ?? Math.max(m.getZoom(), 16.6);
    const p = m.project([lng, lat]);
    const cv = m.getCanvas();
    const w = cv.clientWidth, h = cv.clientHeight;
    const near = p.x > -w * 0.25 && p.y > -h * 0.25 && p.x < w * 1.25 && p.y < h * 1.25 && Math.abs(z - m.getZoom()) < 3;
    const opts = { center: [lng, lat], zoom: z, padding: padding ?? m.getPadding(), essential: true };
    if (reducedMotion()) m.jumpTo(opts);
    else if (near) m.easeTo({ ...opts, duration: 620, easing: easeOutCubic });
    else m.flyTo({ ...opts, speed: 1.5, curve: 1.42, maxDuration: 2300 });
  }

  fitBBox(bb, { padding, maxZoom = 18 } = {}) {
    const m = this.map;
    const pad = padding ?? m.getPadding();
    const opts = { padding: { top: (pad.top || 0) + 40, bottom: (pad.bottom || 0) + 40, left: (pad.left || 0) + 40, right: (pad.right || 0) + 40 }, maxZoom, duration: reducedMotion() ? 0 : 900, essential: true };
    // avoid padding larger than the viewport (tiny screens)
    const cv = m.getCanvas();
    if (opts.padding.left + opts.padding.right > cv.clientWidth - 40) opts.padding.left = opts.padding.right = 20;
    if (opts.padding.top + opts.padding.bottom > cv.clientHeight - 40) opts.padding.top = opts.padding.bottom = 20;
    m.fitBounds([[bb[0], bb[1]], [bb[2], bb[3]]], opts);
  }

  setPadding(padding, animate = false) {
    const m = this.map;
    if (!m) return;
    if (animate && !reducedMotion()) m.easeTo({ padding, duration: 360, easing: easeOutCubic });
    else m.setPadding(padding);
  }
}
